'use client'

import React from 'react'

import {
  Box,
  HTMLChakraProps,
  Image,
  Link,
  Text,
  chakra,
} from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { ChevronDownIcon } from '../Icon/ChevronDownIcon'
import { ChevronUpIcon } from '../Icon/ChevronUpIcon'
import {
  answerGridStyles,
  answerLabelStyles,
  answerValueStyles,
  collapsibleContentStyles,
  expandToggleStyles,
  fileCountStyles,
  fileImageStyles,
  fileLinkStyles,
  filePreviewContainerStyles,
  filePreviewGridStyles,
  rootStyles,
  sectionHeaderStyles,
  seeMoreToggleStyles,
} from './RecordedFormResponses.styles'
import type { RecordedFormResponsesSize } from './RecordedFormResponses.styles'

export type { ColorScheme, RecordedFormResponsesSize }

export type FormResponseAnswer = {
  /** Unique identifier for the question */
  questionId: string
  /** Display label for the question */
  label: string
  /** Formatted display value for the answer */
  displayValue?: string | null
  /** Answer type — used to determine rendering (e.g., file uploads) */
  answerType?: string | null
  /** Optional section name for grouping answers */
  sectionName?: string | null
  /** File URLs for file upload answers */
  fileUrls?: string[] | null
}

export const DEFAULT_COLLAPSED_MAX_HEIGHT = 240

export type RecordedFormResponsesProps = Omit<
  HTMLChakraProps<'div'>,
  'children'
> & {
  colorScheme?: ColorScheme
  /** List of question-answer pairs to display */
  answers: FormResponseAnswer[]
  /** Label for answers that have no section name */
  defaultSectionLabel?: string
  /** Size variant — 'sm' matches IncidentCard description font, 'md' is default */
  size?: RecordedFormResponsesSize
  /**
   * Max rendered height (px) while collapsed. When the responses measure
   * taller than this, the content clips at this height and two toggles
   * render: a chevron in the top-right corner (down to expand, up to
   * collapse) and a see-more/see-less text button at the bottom center.
   * The toggles only appear when the content actually exceeds the max.
   * Pass `null` to always render the full form.
   */
  collapsedMaxHeight?: number | null
  /** Label for the bottom text button while collapsed */
  seeMoreLabel?: string
  /** Label for the bottom text button while expanded */
  seeLessLabel?: string
}

export const RecordedFormResponses = ({
  colorScheme = 'dark',
  answers,
  defaultSectionLabel = 'General',
  size = 'md',
  collapsedMaxHeight = DEFAULT_COLLAPSED_MAX_HEIGHT,
  seeMoreLabel = 'See More',
  seeLessLabel = 'See Less',
  ...rest
}: RecordedFormResponsesProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isOverflowing, setIsOverflowing] = React.useState(false)

  const isCollapsible = collapsedMaxHeight != null

  // scrollHeight reports the full content height even while the wrapper
  // clips, so one measurement works in both states. The ResizeObserver
  // re-measures as async content (file previews) changes the height.
  React.useEffect(() => {
    if (!isCollapsible) return undefined
    const node = contentRef.current
    if (!node) return undefined

    const measure = () =>
      setIsOverflowing(node.scrollHeight > collapsedMaxHeight)

    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [isCollapsible, collapsedMaxHeight, answers])

  if (!answers.length) return null

  // A Map, not a plain object: sectionName is user-authored form content, and
  // a name colliding with an inherited object key ('__proto__', 'constructor')
  // would make the truthy inherited value skip initialization and crash on
  // .push() at render time.
  const answersBySection = answers.reduce((acc, answer) => {
    const section = answer.sectionName || defaultSectionLabel
    const sectionAnswers = acc.get(section)
    if (sectionAnswers) {
      sectionAnswers.push(answer)
    } else {
      acc.set(section, [answer])
    }
    return acc
  }, new Map<string, FormResponseAnswer[]>())

  const isClipped = isCollapsible && !isExpanded
  const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon
  const toggleExpanded = () => setIsExpanded((wasExpanded) => !wasExpanded)

  return (
    <Box {...rootStyles(size)} {...rest}>
      {isCollapsible && isOverflowing && (
        <chakra.button
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? 'Collapse form responses' : 'Show all form responses'
          }
          onClick={toggleExpanded}
          type='button'
          {...expandToggleStyles(colorScheme)}
        >
          <ChevronIcon height={16} width={16} />
        </chakra.button>
      )}
      <Box
        ref={contentRef}
        {...collapsibleContentStyles(size, isClipped, collapsedMaxHeight ?? 0)}
      >
        {Array.from(answersBySection.entries()).map(
          ([sectionName, sectionAnswers]) => (
            <Box key={sectionName}>
              <Text {...sectionHeaderStyles(colorScheme)}>{sectionName}</Text>
              <Box {...answerGridStyles(size)}>
                {sectionAnswers.map((answer) => (
                  <React.Fragment key={answer.questionId}>
                    <Text {...answerLabelStyles(colorScheme, size)}>
                      {answer.label}
                    </Text>
                    <Box>
                      <AnswerValue
                        answer={answer}
                        colorScheme={colorScheme}
                        size={size}
                      />
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )
        )}
      </Box>
      {isCollapsible && isOverflowing && (
        <chakra.button
          aria-expanded={isExpanded}
          onClick={toggleExpanded}
          type='button'
          {...seeMoreToggleStyles(colorScheme, size)}
        >
          {isExpanded ? seeLessLabel : seeMoreLabel}
        </chakra.button>
      )}
    </Box>
  )
}

// ─── Answer value renderer ──────────────────────────────────────────────────

type AnswerValueProps = {
  answer: FormResponseAnswer
  colorScheme: ColorScheme
  size: RecordedFormResponsesSize
}

const FILE_UPLOAD_TYPE = 'FILE_UPLOAD'

const AnswerValue = ({ answer, colorScheme, size }: AnswerValueProps) => {
  if (answer.answerType === FILE_UPLOAD_TYPE && answer.fileUrls?.length) {
    return (
      <Box {...filePreviewContainerStyles}>
        {answer.displayValue && (
          <Text {...fileCountStyles(colorScheme, size)}>
            {answer.displayValue}
          </Text>
        )}
        <Box {...filePreviewGridStyles}>
          {answer.fileUrls.map((url, index) => (
            <FilePreview
              colorScheme={colorScheme}
              key={index}
              size={size}
              url={url}
            />
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Text {...answerValueStyles(colorScheme, size)}>
      {answer.displayValue || '-'}
    </Text>
  )
}

// ─── File preview ───────────────────────────────────────────────────────────

type FilePreviewProps = {
  url: string
  colorScheme: ColorScheme
  size: RecordedFormResponsesSize
}

const isImageUrl = (url: string) =>
  /\.(jpg|jpeg|png)/i.test(url) || url.includes('image')

// fileUrls are presigned S3 URLs whose object key is followed by a long
// query string — strip query/hash so the label is the filename, not a
// truncated signature (e.g. 'incident-doc.pdf?X-A').
const getFileLabel = (url: string) =>
  url.split(/[?#]/)[0].split('/').pop()?.slice(0, 20)

const FilePreview = ({ url, colorScheme, size }: FilePreviewProps) => {
  if (isImageUrl(url)) {
    return (
      <Link href={url} rel='noopener noreferrer' target='_blank'>
        <Image alt='Uploaded file' {...fileImageStyles(size)} src={url} />
      </Link>
    )
  }

  return (
    <Link
      href={url}
      rel='noopener noreferrer'
      target='_blank'
      {...fileLinkStyles(colorScheme, size)}
    >
      {getFileLabel(url)}
    </Link>
  )
}
